import { createContext, useContext, useId, useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Collapse } from "./Collapse";

// The reference "AI thought chain" as a spark primitive: numbered stages with a
// status icon, a foldable title and the stage's items underneath. The visual
// comes from the reference (first ported in the Studio prototype), the list
// semantics from ImportPipelineBar (<ol>, aria-current, state in words). It
// knows nothing about where its data comes from; OperationProgressView maps
// the progress contract onto it.
//
// "horizontal" lays the stages out in a row once the <main> container is
// wide enough and falls back to the vertical rail below that, so a squeezed
// page (open assistant drawer) reflows instead of clipping
// (@adr SPARK-0020-container-queries-as-the-responsive-primitive-inside-main@2).
export type StepChainStatus = "done" | "active" | "pending" | "failed" | "skipped";
export type StepChainOrientation = "vertical" | "horizontal";

const ChainContext = createContext<StepChainOrientation>("vertical");

const StepContext = createContext<{
  open: boolean;
  toggle: () => void;
  status: StepChainStatus;
  contentId: string;
  collapsible: boolean;
} | null>(null);

function useStep() {
  const step = useContext(StepContext);
  if (!step) throw new Error("StepChain parts belong inside a StepChainStep");
  return step;
}

const LABEL_TONE: Record<StepChainStatus, string> = {
  done: "text-ink-muted",
  active: "text-ink",
  pending: "text-ink-muted",
  failed: "text-ink",
  skipped: "text-ink-muted",
};

const STATE_LABEL = {
  done: { id: "design.stepChainStateDone", defaultMessage: "done" },
  active: { id: "design.stepChainStateActive", defaultMessage: "in progress" },
  pending: { id: "design.stepChainStatePending", defaultMessage: "pending" },
  failed: { id: "design.stepChainStateFailed", defaultMessage: "failed" },
  skipped: { id: "design.stepChainStateSkipped", defaultMessage: "skipped" },
} as const satisfies Record<StepChainStatus, { id: string; defaultMessage: string }>;

function StatusIcon({ status }: { status: StepChainStatus }) {
  if (status === "done") {
    return (
      <span className="flex size-5 items-center justify-center rounded-full bg-accent">
        <svg viewBox="0 0 24 24" className="size-3 text-ink" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M20 6 9 17l-5-5" />
        </svg>
      </span>
    );
  }
  if (status === "active") {
    return (
      <svg viewBox="0 0 24 24" className="size-5 animate-spin stroke-accent-decoration motion-reduce:animate-none" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      </svg>
    );
  }
  if (status === "failed") {
    return (
      <span className="flex size-5 items-center justify-center rounded-full text-smycles-micro font-semibold text-ink ring-1 ring-ink-ui" aria-hidden="true">
        ✕
      </span>
    );
  }
  if (status === "skipped") {
    return (
      <svg viewBox="0 0 24 24" className="size-5 text-ink-muted" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <path d="M8 12h8" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" className="size-5 text-ink-muted" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
    </svg>
  );
}

export function StepChain({
  label,
  orientation = "vertical",
  children,
}: {
  // Names the list for assistive tech ("Match progress"); the stages carry the rest.
  label: string;
  orientation?: StepChainOrientation;
  children: ReactNode;
}) {
  const horizontal = orientation === "horizontal";
  return (
    <ChainContext.Provider value={orientation}>
      <ol aria-label={label} className={`flex flex-col ${horizontal ? "@3xl:flex-row @3xl:gap-4" : ""}`}>
        {children}
      </ol>
    </ChainContext.Provider>
  );
}

export function StepChainStep({
  children,
  status = "pending",
  defaultOpen = true,
  collapsible = true,
}: {
  children: ReactNode;
  status?: StepChainStatus;
  defaultOpen?: boolean;
  // A stage without items has nothing to fold: no chevron, always "open".
  collapsible?: boolean;
}) {
  const horizontal = useContext(ChainContext) === "horizontal";
  const [open, setOpen] = useState(defaultOpen);
  const contentId = useId();
  const doneLine = status === "done" ? "bg-accent" : "bg-ink/10";
  return (
    <StepContext.Provider value={{ open, toggle: () => setOpen((value) => !value), status, contentId, collapsible }}>
      <li
        aria-current={status === "active" ? "step" : undefined}
        // In a row every stage but the last grows, so the connectors share out the width and the last
        // stage, which has no connector after it, ends flush with the container.
        className={`group/step flex gap-3.5 ${horizontal ? "@3xl:min-w-0 @3xl:flex-1 @3xl:flex-col @3xl:gap-2 @3xl:last:flex-none" : ""}`}
      >
        <div className={`flex shrink-0 flex-col items-center ${horizontal ? "@3xl:flex-row" : ""}`}>
          <span className={`mt-0.5 ${horizontal ? "@3xl:mt-0" : ""}`}>
            <StatusIcon status={status} />
          </span>
          <span
            aria-hidden="true"
            className={`mt-1.5 min-h-5 w-0.5 flex-1 rounded-sm ${doneLine} ${
              horizontal ? "@3xl:mt-0 @3xl:ml-2 @3xl:h-0.5 @3xl:min-h-0 @3xl:w-auto @3xl:group-last/step:invisible" : ""
            }`}
          />
        </div>
        <div className={`min-w-0 flex-1 pb-2 ${horizontal ? "@3xl:pb-0" : ""}`}>{children}</div>
      </li>
    </StepContext.Provider>
  );
}

export function StepChainTrigger({
  children,
  meta,
}: {
  children: ReactNode;
  // A quiet fact after the title, such as how long the stage has been running. It sits outside the
  // toggle button, so a value that ticks does not keep renaming the button for assistive tech.
  meta?: ReactNode;
}) {
  const intl = useIntl();
  const { open, toggle, status, contentId, collapsible } = useStep();
  const label = (
    <span className={`text-smycles-body font-semibold tracking-tight ${LABEL_TONE[status]}`}>
      {status === "active" && typeof children === "string" ? <span className="smycles-shimmer">{children}</span> : children}
      <span className="sr-only">: {intl.formatMessage(STATE_LABEL[status])}</span>
    </span>
  );
  const badge =
    status === "active" ? (
      <span className="rounded-full px-2 py-0.5 text-smycles-micro font-semibold text-ink-accent ring-1 ring-accent-decoration" aria-hidden="true">
        <FormattedMessage id="design.stepChainRunning" defaultMessage="In progress" />
      </span>
    ) : status === "failed" ? (
      <span className="rounded-full px-2 py-0.5 text-smycles-micro font-semibold text-ink ring-1 ring-ink-ui" aria-hidden="true">
        <FormattedMessage id="design.stepChainFailed" defaultMessage="Failed" />
      </span>
    ) : null;
  const aside = meta != null ? <span className="text-smycles-meta tabular-nums text-ink-muted">{meta}</span> : null;
  if (!collapsible) return <div className="flex flex-wrap items-center gap-1.5">{label}{badge}{aside}</div>;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={contentId}
        className="flex cursor-pointer select-none flex-wrap items-center gap-1.5 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui"
      >
        {label}
        <span
          className={`flex items-center text-ink-muted transition-transform duration-[var(--duration-base)] ease-in-out motion-reduce:transition-none ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        >
          <svg viewBox="0 0 24 24" className="size-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
        {badge}
      </button>
      {aside}
    </div>
  );
}

export function StepChainContent({ children }: { children: ReactNode }) {
  const { open, contentId } = useStep();
  return (
    <div id={contentId}>
      <Collapse open={open}>
        <div className="mt-1 pl-0.5">{children}</div>
      </Collapse>
    </div>
  );
}

export function StepChainItem({ children }: { children: ReactNode }) {
  return (
    <div className="smycles-item-in flex items-start gap-2 py-1">
      <span className="mt-2 size-1 shrink-0 rounded-full bg-ink/40" aria-hidden="true" />
      <span className="text-smycles-meta leading-relaxed text-ink-muted">{children}</span>
    </div>
  );
}
